<template>
    <div class="d-flex gap-1">
        <span :class="timeCssMarker">{{ time }}</span>

        <MatrixSpeedLimitWarning
            v-if="matrixStationRoleCell.MatrixConfig.SpeedLimitKph > 0"
            :matrixStationRoleCell="matrixStationRoleCell"
        />

        <MatrixStartControlWarning
            v-if="matrixStationRoleCell.HasStartControlWarning"
            :matrixStationRoleCell="matrixStationRoleCell"
            :row="row"
        />

        <MatrixJumpStartWarning
            v-if="matrixStationRoleCell.HasJumpStartWarning"
            :matrixStationRoleCell="matrixStationRoleCell"
        />
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import type MatrixRow from '../../classes/MatrixRow';
import type MatrixStationRoleCell from '../../classes/MatrixStationRoleCell';
import { formatDateDefault, millisecondsToFormattedTime } from '@/utilities/dateutils';
import MatrixSpeedLimitWarning from '../MatrixWarnings/MatrixSpeedLimitWarning.vue';
import MatrixStartControlWarning from '../MatrixWarnings/MatrixStartControlWarning.vue';
import MatrixJumpStartWarning from '../MatrixWarnings/MatrixJumpStartWarning.vue';

const props = defineProps<{ matrixStationRoleCell: MatrixStationRoleCell; row: MatrixRow }>();

const time = computed(() => {
    if (props.matrixStationRoleCell.hasRaceContestantTime()) {
        // Time as X
        if (props.matrixStationRoleCell.MatrixConfig.TimeDisplay === 'TimeAsX') {
            return 'X';
        }

        // ClockTime
        if (props.matrixStationRoleCell.MatrixConfig.TimeDisplay === 'ClockTime' || props.matrixStationRoleCell.IsStart) {
            return formatDateDefault(
                props.matrixStationRoleCell.RaceContestantTimeClockTime,
                props.matrixStationRoleCell.MatrixConfig.TimeDecimals,
            );
        }

        // Split
        if (props.matrixStationRoleCell.MatrixConfig.TimeDisplay === 'SplitTime') {
            if (props.matrixStationRoleCell.SplitTime === null) {
                return '';
            }
            return millisecondsToFormattedTime(
                props.matrixStationRoleCell.SplitTime!,
                props.matrixStationRoleCell.MatrixConfig.TimeDecimals,
            );
        }

        // UsedTime
        return millisecondsToFormattedTime(
            props.matrixStationRoleCell.RaceContestantTimeUsedTime!,
            props.matrixStationRoleCell.MatrixConfig.TimeDecimals,
        );
    } else if (props.matrixStationRoleCell.hasContestantTime()) {
        if (props.matrixStationRoleCell.MatrixConfig.HideContestantTimes && !props.matrixStationRoleCell.IsStartControl) {
            return '';
        }
        return formatDateDefault(props.matrixStationRoleCell.ContestantClockTime, props.matrixStationRoleCell.MatrixConfig.TimeDecimals);
    } else if (props.matrixStationRoleCell.hasBackupTime()) {
        return formatDateDefault(props.matrixStationRoleCell.BackupClockTime, props.matrixStationRoleCell.MatrixConfig.TimeDecimals);
    }

    return '';
});

const timeCssMarker = computed(() => {
    if (props.matrixStationRoleCell.hasRaceContestantTime()) {
        return undefined;
    }

    if (props.matrixStationRoleCell.hasBackupTime()) {
        return 'text-cyan-500';
    }

    if (props.matrixStationRoleCell.MatrixConfig.HideContestantTimes && !props.matrixStationRoleCell.IsStartControl) {
        return undefined;
    }

    if (props.matrixStationRoleCell.hasContestantTime()) {
        return 'text-red-500';
    }

    return undefined;
});
</script>
